/**
 * Font Awesome icon-string parsing — the single source of truth for turning a
 * narrative `icon` string into a Font Awesome class name.
 *
 * Pure and browser-safe (no fs / yaml): used both by the runtime `<Icon>`
 * component (narrative-player) and the node-only lint validator (`./icons`).
 *
 * The app loads Font Awesome via the kit's Web Fonts + CSS embed, so icons
 * render as `<i class="fausb fa-{name}">`. The kit contains a single family
 * style — utility semibold, prefix `fausb` — so every icon normalizes to that
 * prefix regardless of the prefix written in the narrative.
 */

/** The one family-style prefix the kit ships (utility semibold). */
export const KIT_PREFIX = "fausb";

/**
 * Parse a Font Awesome icon string and return the bare icon name (kebab-case,
 * no `fa-` prefix, no family prefix), or `null` for empty/missing input.
 *
 * Accepts the same forms the codebase has always used:
 * - "fass envelope"  (family prefix + name)
 * - "envelope"       (name only)
 * - "fa-envelope"    (leading fa- is stripped)
 * - "fausb fa-envelope"
 */
export function parseIconName(
  iconString: string | undefined | null,
): string | null {
  if (!iconString) return null;

  const parts = iconString.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === "") return null;

  // Two+ tokens: first is the family prefix, second is the name.
  // One token: it's the name (which may carry a leading fa-).
  const name = (parts.length >= 2 ? parts[1] : parts[0]).replace(/^fa-/, "");
  return name || null;
}

/**
 * Turn a narrative icon string into the Font Awesome class string the kit CSS
 * renders, e.g. `"fass envelope"` → `"fausb fa-envelope"`. Returns `null` for
 * empty/missing input.
 *
 * The prefix is always normalized to the kit's single family style, so the
 * caller never has to know which prefix a narrative author wrote.
 */
export function getFAIconClass(
  iconString: string | undefined | null,
): string | null {
  const name = parseIconName(iconString);
  return name ? `${KIT_PREFIX} fa-${name}` : null;
}
